import React, { useRef, useState } from 'react';
import { gql, useMutation, useLazyQuery } from '@apollo/client';
import DialogBase from '../../../base/DialogBase/DialogBase';
import { Upload, CheckCircle2, XCircle, AlertCircle, Image as ImageIcon, FolderOpen, Loader2 } from 'lucide-react';

const GET_CONTENTS_FOR_MATCHING = gql`
    query getContentsForThumbnailMatch($libraryId: String) {
        Library(id: $libraryId) {
            directContents(first: 10000) {
                nodes {
                    id
                    name
                    category
                    currentVersionId
                }
            }
        }
    }
`;

// Types live on ContentVersion, not on Content in a bulk list context.
// Fetch them separately using the collected currentVersionIds.
const GET_VERSION_TYPES = gql`
    query getVersionTypesForThumbnails($versionIds: [String]) {
        ContentVersionList(
            first: 10000
            where: { id: { in: $versionIds } }
        ) {
            nodes {
                id
                types {
                    id
                    name
                }
            }
        }
    }
`;

const SET_CONTENT_THUMBNAIL = gql`
    mutation setContentThumbnailBulk($contentId: String, $name: String) {
        Content(id: $contentId) {
            setThumbnail(name: $name)
        }
    }
`;

const SET_CONTENT_TYPE_THUMBNAIL = gql`
    mutation setContentTypeThumbnailBulk($contentId: String, $typeId: String, $name: String) {
        Content(id: $contentId) {
            setTypeThumbnail(typeId: $typeId, name: $name)
        }
    }
`;

const IMAGE_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg']);

type ParsedItem = {
    file: File;
    relativePath: string;
    category: string;
    contentName: string;
    typeName: string | null;
};

type MatchMode = 'exact' | 'name-only' | null;

type MatchedItem = ParsedItem & {
    contentId: string | null;
    typeId: string | null;
    matched: boolean;
    matchMode: MatchMode;
    matchNote?: string;
    status: 'pending' | 'uploading' | 'done' | 'error';
    error?: string;
};

type Phase = 'select' | 'preview' | 'uploading' | 'done';

function parseRelativePath(file: File): ParsedItem | null {
    const relativePath = (file as any).webkitRelativePath as string | undefined;
    if (!relativePath) return null;

    // Split path and drop the root folder name (index 0)
    const parts = relativePath.split('/');
    const relevant = parts.slice(1);

    if (relevant.length < 2) return null;

    const fileName = relevant[relevant.length - 1];
    const lastDot = fileName.lastIndexOf('.');
    if (lastDot < 0) return null;
    const ext = fileName.substring(lastDot).toLowerCase();
    if (!IMAGE_EXTENSIONS.has(ext)) return null;
    const nameWithoutExt = fileName.substring(0, lastDot);

    if (relevant.length === 2) {
        // Category/ContentName.ext → Content-level thumbnail
        return {
            file,
            relativePath,
            category: relevant[0],
            contentName: nameWithoutExt,
            typeName: null,
        };
    }

    if (relevant.length === 3) {
        // Category/ContentName/TypeName.ext → Content Type thumbnail
        return {
            file,
            relativePath,
            category: relevant[0],
            contentName: relevant[1],
            typeName: nameWithoutExt,
        };
    }

    return null;
}

function matchItems(
    parsed: ParsedItem[],
    libraryContents: any[]
): MatchedItem[] {
    const normalize = (s: string) => s.toLowerCase().trim();

    return parsed.map((item) => {
        const contentNameNorm = normalize(item.contentName);
        const categoryNorm = normalize(item.category);

        // 1. Exact match: category + name
        let content = libraryContents.find(
            (c) =>
                normalize(c.name ?? '') === contentNameNorm &&
                normalize(c.category ?? '') === categoryNorm
        );
        let matchMode: MatchMode = content ? 'exact' : null;

        // 2. Fallback: name-only match (category mismatch is common when Revit
        //    categories differ from library category values)
        if (!content) {
            const nameMatches = libraryContents.filter(
                (c) => normalize(c.name ?? '') === contentNameNorm
            );
            if (nameMatches.length === 1) {
                content = nameMatches[0];
                matchMode = 'name-only';
            } else if (nameMatches.length > 1) {
                return {
                    ...item,
                    contentId: null,
                    typeId: null,
                    matched: false,
                    matchMode: null,
                    matchNote: `${nameMatches.length} contents share this name — ambiguous`,
                    status: 'pending' as const,
                };
            }
        }

        if (!content) {
            return {
                ...item,
                contentId: null,
                typeId: null,
                matched: false,
                matchMode: null,
                matchNote: `No content named "${item.contentName}" found in library`,
                status: 'pending' as const,
            };
        }

        if (!item.typeName) {
            return { ...item, contentId: content.id, typeId: null, matched: true, matchMode, status: 'pending' as const };
        }

        const typeNameNorm = normalize(item.typeName);
        const type = (content.types ?? []).find(
            (t: any) => normalize(t?.name ?? '') === typeNameNorm
        );

        return {
            ...item,
            contentId: content.id,
            typeId: type?.id ?? null,
            matched: !!type,
            matchMode: type ? matchMode : null,
            matchNote: type ? undefined : `Type "${item.typeName}" not found on content "${content.name}"`,
            status: 'pending' as const,
        };
    });
}

async function uploadToSas(sasUrl: string, file: File): Promise<void> {
    const response = await fetch(sasUrl, {
        method: 'PUT',
        headers: { 'x-ms-blob-type': 'BlockBlob' },
        body: file,
    });
    if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
}

type Props = {
    libraryId: string;
    visible: boolean;
    onClose: () => void;
};

const BulkThumbnailUploadDialog = ({ libraryId, visible, onClose }: Props) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [phase, setPhase] = useState<Phase>('select');
    const [items, setItems] = useState<MatchedItem[]>([]);
    const [uploadError, setUploadError] = useState('');

    const [fetchContents, contentsResult] = useLazyQuery(GET_CONTENTS_FOR_MATCHING, {
        fetchPolicy: 'network-only',
    });
    const [fetchVersionTypes] = useLazyQuery(GET_VERSION_TYPES, {
        fetchPolicy: 'network-only',
    });
    const [setContentThumbnail] = useMutation(SET_CONTENT_THUMBNAIL);
    const [setContentTypeThumbnail] = useMutation(SET_CONTENT_TYPE_THUMBNAIL);

    const handleFolderChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? []);
        if (files.length === 0) return;

        setUploadError('');

        const parsed = files.map(parseRelativePath).filter((x): x is ParsedItem => x !== null);
        if (parsed.length === 0) {
            setUploadError('No image files were found in the expected folder structure (Category/ContentName.ext or Category/ContentName/TypeName.ext).');
            return;
        }

        // Step 1: load all direct content (name, category, currentVersionId)
        const contentResult = await fetchContents({ variables: { libraryId } });
        const contentNodes: any[] = contentResult.data?.Library?.directContents?.nodes ?? [];

        // Step 2: batch-load types from each content's current ContentVersion
        const versionIds = contentNodes.map((c: any) => c.currentVersionId).filter(Boolean);
        const versionResult = await fetchVersionTypes({ variables: { versionIds } });
        const versionNodes: any[] = versionResult.data?.ContentVersionList?.nodes ?? [];

        // Build versionId → types map
        const versionTypesMap = new Map<string, any[]>();
        for (const v of versionNodes) {
            if (v?.id) versionTypesMap.set(v.id, v.types ?? []);
        }

        // Merge types onto content nodes
        const libraryContents = contentNodes.map((c: any) => ({
            ...c,
            types: versionTypesMap.get(c.currentVersionId) ?? [],
        }));

        const matched = matchItems(parsed, libraryContents);
        setItems(matched);
        setPhase('preview');
    };

    const handleUpload = async () => {
        const toUpload = items.filter((i) => i.matched);
        if (toUpload.length === 0) return;

        setPhase('uploading');
        setUploadError('');

        const updated = [...items];

        for (let i = 0; i < updated.length; i++) {
            const item = updated[i];
            if (!item.matched) continue;

            updated[i] = { ...item, status: 'uploading' };
            setItems([...updated]);

            try {
                let sasUrl: string | null = null;

                if (item.typeName && item.typeId) {
                    const res = await setContentTypeThumbnail({
                        variables: { contentId: item.contentId, typeId: item.typeId, name: item.file.name },
                    });
                    sasUrl = res.data?.Content?.setTypeThumbnail ?? null;
                } else {
                    const res = await setContentThumbnail({
                        variables: { contentId: item.contentId, name: item.file.name },
                    });
                    sasUrl = res.data?.Content?.setThumbnail ?? null;
                }

                if (!sasUrl) throw new Error('No upload URL returned from server.');

                await uploadToSas(sasUrl, item.file);
                updated[i] = { ...updated[i], status: 'done' };
            } catch (err: any) {
                updated[i] = { ...updated[i], status: 'error', error: err?.message ?? 'Upload failed.' };
            }

            setItems([...updated]);
        }

        setPhase('done');
    };

    const handleReset = () => {
        setPhase('select');
        setItems([]);
        setUploadError('');
        if (inputRef.current) inputRef.current.value = '';
    };

    const matchedCount = items.filter((i) => i.matched).length;
    const unmatchedCount = items.filter((i) => !i.matched).length;
    const doneCount = items.filter((i) => i.status === 'done').length;
    const errorCount = items.filter((i) => i.status === 'error').length;

    const getTitle = () => {
        if (phase === 'done') return 'Upload Complete';
        return 'Bulk Upload Thumbnails';
    };

    const getActions = () => {
        if (phase === 'select') {
            return (
                <button className="btn btn-secondary" onClick={onClose}>
                    Cancel
                </button>
            );
        }
        if (phase === 'preview') {
            return (
                <>
                    <button className="btn btn-secondary" onClick={handleReset}>
                        Back
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleUpload}
                        disabled={matchedCount === 0}
                    >
                        <Upload size={15} />
                        Upload {matchedCount} thumbnail{matchedCount !== 1 ? 's' : ''}
                    </button>
                </>
            );
        }
        if (phase === 'done') {
            return (
                <>
                    <button className="btn btn-secondary" onClick={handleReset}>
                        Upload More
                    </button>
                    <button className="btn btn-primary" onClick={onClose}>
                        Close
                    </button>
                </>
            );
        }
        return null;
    };

    return (
        <DialogBase
            title={getTitle()}
            type="Custom"
            visible={visible}
            onClose={onClose}
            actions={getActions()}
            validationError={uploadError}
            width="680px"
            isLoading={contentsResult.loading}
        >
            {/* Phase: select */}
            {phase === 'select' && (
                <div className="flex flex-col items-center gap-5 py-6">
                    <div
                        className="flex flex-col items-center justify-center rounded-xl p-8 w-full"
                        style={{ border: '2px dashed var(--border-default)', background: 'var(--bg-subtle)' }}
                    >
                        <FolderOpen size={40} style={{ color: 'var(--text-tertiary)' }} className="mb-3" />
                        <p className="text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                            Select a thumbnail folder
                        </p>
                        <p className="text-xs text-center mb-4" style={{ color: 'var(--text-tertiary)', maxWidth: '400px' }}>
                            Organise thumbnails as{' '}
                            <code className="px-1 rounded" style={{ background: 'var(--bg-hover)' }}>
                                Category\Content Name.ext
                            </code>{' '}
                            for content thumbnails, or{' '}
                            <code className="px-1 rounded" style={{ background: 'var(--bg-hover)' }}>
                                Category\Content Name\Type Name.ext
                            </code>{' '}
                            for content type thumbnails.
                        </p>
                        <input
                            ref={inputRef}
                            type="file"
                            className="hidden"
                            // @ts-ignore – webkitdirectory is non-standard but widely supported
                            webkitdirectory=""
                            multiple
                            accept="image/*"
                            onChange={handleFolderChange}
                        />
                        <button
                            className="btn btn-primary"
                            onClick={() => inputRef.current?.click()}
                        >
                            <FolderOpen size={15} /> Choose Folder
                        </button>
                    </div>

                    <div className="text-xs w-full" style={{ color: 'var(--text-tertiary)' }}>
                        <p className="font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>Expected folder structure</p>
                        <div
                            className="rounded-lg p-3 font-mono text-xs leading-relaxed"
                            style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border-default)' }}
                        >
                            <div>📁 Thumbnails/</div>
                            <div className="pl-4">📁 Doors/</div>
                            <div className="pl-8">🖼️ Door-1.png <span style={{ color: 'var(--text-tertiary)' }}>← content thumbnail</span></div>
                            <div className="pl-8">📁 Door-1/</div>
                            <div className="pl-12">🖼️ Single Leaf.png <span style={{ color: 'var(--text-tertiary)' }}>← type thumbnail</span></div>
                            <div className="pl-12">🖼️ Double Leaf.png</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Phase: preview */}
            {phase === 'preview' && (
                <div className="flex flex-col gap-4">
                    {/* Summary */}
                    <div className="flex items-center gap-3">
                        <div
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm"
                            style={{ background: 'var(--success-soft)', color: 'var(--success)' }}
                        >
                            <CheckCircle2 size={14} />
                            {matchedCount} matched
                        </div>
                        {unmatchedCount > 0 && (
                            <div
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm"
                                style={{ background: 'var(--warning-soft)', color: 'var(--warning)' }}
                            >
                                <AlertCircle size={14} />
                                {unmatchedCount} unmatched
                            </div>
                        )}
                        <span className="text-xs ml-auto" style={{ color: 'var(--text-tertiary)' }}>
                            Only matched items will be uploaded.
                        </span>
                    </div>

                    {/* Table */}
                    <div className="overflow-auto rounded-lg" style={{ maxHeight: '360px', border: '1px solid var(--border-default)' }}>
                        <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border-default)' }}>
                                    <th className="text-left px-3 py-2 text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>File</th>
                                    <th className="text-left px-3 py-2 text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Target</th>
                                    <th className="text-left px-3 py-2 text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, i) => (
                                    <tr
                                        key={i}
                                        style={{
                                            borderBottom: '1px solid var(--border-subtle)',
                                            background: item.matched ? undefined : 'var(--warning-soft)',
                                        }}
                                    >
                                        <td className="px-3 py-2" style={{ color: 'var(--text-primary)' }}>
                                            <div className="flex items-center gap-2">
                                                <ImageIcon size={13} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                                                <span className="font-mono text-xs truncate" style={{ maxWidth: '200px' }} title={item.relativePath}>
                                                    {item.relativePath}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                                            {item.typeName
                                                ? <span><span style={{ color: 'var(--text-tertiary)' }}>type /</span> {item.typeName}</span>
                                                : <span><span style={{ color: 'var(--text-tertiary)' }}>content /</span> {item.contentName}</span>
                                            }
                                        </td>
                                        <td className="px-3 py-2">
                                            {item.matched && item.matchMode === 'exact' && (
                                                <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--success)' }}>
                                                    <CheckCircle2 size={12} /> Matched
                                                </span>
                                            )}
                                            {item.matched && item.matchMode === 'name-only' && (
                                                <span className="flex items-center gap-1 text-xs" title="Matched by name; folder category differs from library category">
                                                    <CheckCircle2 size={12} style={{ color: 'var(--warning)' }} />
                                                    <span style={{ color: 'var(--warning)' }}>Matched (category mismatch)</span>
                                                </span>
                                            )}
                                            {!item.matched && (
                                                <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--danger)' }} title={item.matchNote}>
                                                    <AlertCircle size={12} /> {item.matchNote ?? 'Not found in library'}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Phase: uploading / done */}
            {(phase === 'uploading' || phase === 'done') && (
                <div className="flex flex-col gap-3">
                    {/* Summary for done */}
                    {phase === 'done' && (
                        <div className="flex items-center gap-3 mb-2">
                            <div
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm"
                                style={{ background: 'var(--success-soft)', color: 'var(--success)' }}
                            >
                                <CheckCircle2 size={14} />
                                {doneCount} uploaded
                            </div>
                            {errorCount > 0 && (
                                <div
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm"
                                    style={{ background: 'var(--danger-soft)', color: 'var(--danger)' }}
                                >
                                    <XCircle size={14} />
                                    {errorCount} failed
                                </div>
                            )}
                        </div>
                    )}

                    {/* Progress table */}
                    <div className="overflow-auto rounded-lg" style={{ maxHeight: '380px', border: '1px solid var(--border-default)' }}>
                        <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border-default)' }}>
                                    <th className="text-left px-3 py-2 text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>File</th>
                                    <th className="text-left px-3 py-2 text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Result</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.filter((i) => i.matched).map((item, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                        <td className="px-3 py-2 font-mono text-xs" style={{ color: 'var(--text-primary)', maxWidth: '360px' }}>
                                            <span className="truncate block" title={item.relativePath}>{item.relativePath}</span>
                                        </td>
                                        <td className="px-3 py-2">
                                            {item.status === 'pending' && (
                                                <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Waiting…</span>
                                            )}
                                            {item.status === 'uploading' && (
                                                <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--accent)' }}>
                                                    <Loader2 size={12} className="animate-spin" /> Uploading…
                                                </span>
                                            )}
                                            {item.status === 'done' && (
                                                <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--success)' }}>
                                                    <CheckCircle2 size={12} /> Done
                                                </span>
                                            )}
                                            {item.status === 'error' && (
                                                <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--danger)' }} title={item.error}>
                                                    <XCircle size={12} /> {item.error ?? 'Error'}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </DialogBase>
    );
};

export default BulkThumbnailUploadDialog;
